import * as React from 'react';
import styled from 'styled-components';
import EmailIcon from '@material-ui/icons/Email';
import SmsIcon from '@material-ui/icons/Sms';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import { Link } from 'react-router-dom';
import { Chance } from 'chance';
import { getNowDateString } from '../../../shared/utils';
import { RemindersSettingsData, TestCommunication } from '../../../models';
import AppButton from '../../../components/AppButton';

export const chance = new Chance();

// Centralized icon helper function
export const getMessageIcon = (type: 'sms' | 'whatsapp' | 'email', props?: { style?: React.CSSProperties }) => {
    const iconStyle = { fontSize: '1.2rem', verticalAlign: 'middle', color: 'inherit', ...props?.style };
    switch (type) {
        case 'sms':
            return <SmsIcon style={iconStyle} />;
        case 'whatsapp':
            return <WhatsAppIcon style={{ ...iconStyle, color: '#25D366' }} />;
        case 'email':
            return <EmailIcon style={iconStyle} />;
    }
}

// Channel card - clickable header with checkbox and collapsible content
export interface ChannelCardProps extends React.HTMLAttributes<HTMLDivElement> {
    $isActive: boolean;
    $isSelected?: boolean;
}

export const ChannelCard = styled.div.attrs<ChannelCardProps>(props => ({
    ...props,
}))<ChannelCardProps>`
    border: var(--border-thin) solid ${props => props.$isSelected ? 'var(--appBlue)' : 'var(--appLigthGrey)'};
    border-radius: 8px;
    overflow: hidden;
    transition: border-color 0.2s ease;
`;

export interface ChannelHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
    $isActive: boolean;
    $isSelected: boolean;
}

export const ChannelHeader = styled.div.attrs<ChannelHeaderProps>(props => ({
    ...props,
}))<ChannelHeaderProps>`
    display: flex;
    align-items: center;
    padding: var(--spacer-S);
    background-color: ${props => props.$isSelected ? 'var(--bg-hover-light)' : 'var(--bg-light)'};
    cursor: pointer;
    user-select: none;
    transition: background-color 0.2s ease;

    &:hover {
        background-color: var(--bg-hover-light);
    }
`;

export const ChannelInfo = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-XS);
    flex-wrap: wrap;
    flex: 1;
    margin-left: var(--spacer-XS);
`;

export const ChannelContent = styled.div`
    padding: var(--spacer-S);
    border-top: var(--border-thin) solid var(--appLigthGrey);
    display: flex;
    flex-direction: column;
    gap: var(--spacer-S);
`;

export interface ChannelStatusProps extends React.HTMLAttributes<HTMLSpanElement> {
    $isActive: boolean;
}

export const ChannelStatus = styled.span<ChannelStatusProps>`
    font-size: 0.75rem;
    padding: 2px 8px;
    border-radius: 12px;
    background-color: ${props => props.$isActive ? 'var(--success-bg)' : 'transparent'};
    color: ${props => props.$isActive ? 'var(--success-color)' : 'var(--text-muted)'};
    border: var(--border-thin) solid ${props => props.$isActive ? 'var(--success-border)' : 'var(--appLigthGrey)'};
`;

export const ChannelQuota = styled.span`
    font-size: 0.8rem;
    color: var(--text-muted);
`;

export const ChannelWarning = styled.span<React.HTMLAttributes<HTMLSpanElement>>`
    width: 100%;
    font-size: 0.75rem;
    color: var(--danger-color);
`;

export const MySubscriptionLink = styled(Link)`
    color: inherit;
    text-decoration: underline;
`;

export const TestSection = styled.div`
    display: flex;
    gap: var(--spacer-XS);
    align-items: center;
    flex-wrap: wrap;
`;

export const TestActionButton = styled(AppButton)`
    && {
        display: inline-flex;
        align-items: center;
        gap: var(--spacer-XXS);
        white-space: nowrap;
        border: var(--border-thin) solid var(--brand-secondary);
        background-color: var(--brand-accent);
        color: var(--brand-secondary);
        font-weight: 600;
        transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
    }

    &&:hover,
    &&:focus {
        border-color: var(--brand-secondary);
        background-color: var(--surface-brand-soft);
        color: var(--brand-secondary);
    }

    &&:disabled {
        border-color: var(--surface-border-subtle);
        background-color: var(--bg-light);
        color: var(--text-muted);
        opacity: 1;
    }
`;

export const buildBirthdayTestData = (remindersSetting: RemindersSettingsData, receiver: string): TestCommunication => ({
    receiverEmailOrPhoneNumber: receiver,
    remindersSetting: {
        // Map birthday fields to the top-level fields the backend test methods read
        smsText: remindersSetting.birthdayMessage?.smsText,
        emailName: remindersSetting.birthdayMessage?.emailName,
        emailSubject: remindersSetting.birthdayMessage?.emailSubject,
        emailText: remindersSetting.birthdayMessage?.emailText,
        // WhatsApp uses a Meta-approved fixed template — no custom text to override
    },
    appointment: {
        id: '',
        clientId: chance.guid(),
        clientName: chance.name(),
        professionalId: chance.guid(),
        professionalName: chance.name(),
        date: getNowDateString(),
        start: '09:00',
        end: '09:30',
        room: 'Gabinete 1',
        price: 30,
        type: 'Fisioterapia',
        payments: [],
        expenses: [],
        vat: 0,
        confirmationRequestsSentBySms: [],
        confirmationRequestsSentByWhatsapp: [],
    },
    isConfirmationRequest: false,
});

export const buildTestData = (remindersSetting: RemindersSettingsData, receiver: string, isConfirmationRequest: boolean): TestCommunication => ({
    receiverEmailOrPhoneNumber: receiver,
    remindersSetting,
    appointment: {
        id: '',
        clientId: chance.guid(),
        clientName: chance.name(),
        professionalId: chance.guid(),
        professionalName: chance.name(),
        date: getNowDateString(),
        start: '09:00',
        end: '09:30',
        room: 'Gabinete 1',
        price: 30,
        type: 'Fisioterapia',
        payments: [],
        expenses: [],
        vat: 0,
        confirmationRequestsSentBySms: [],
        confirmationRequestsSentByWhatsapp: [],
    },
    isConfirmationRequest,
});

export const normalizeReminderServiceTypesKeys = (reminders: RemindersSettingsData, availableServiceTypeKeys: string[]) =>
    reminders.servicesTypesKeys ?? availableServiceTypeKeys;

export const shouldShowNoServiceTypesWarning = (sendToAllServicesTypes: boolean, selectedServiceTypesKeys: string[]) =>
    !sendToAllServicesTypes && selectedServiceTypesKeys.length === 0;

export const normalizeEmailReminderSettings = (
    reminders: RemindersSettingsData,
    companyName: string,
    emailDefaultSubject: string,
    emailDefaultText: string,
) => ({
    emailName: reminders.emailName || companyName,
    emailSubject: reminders.emailSubject || emailDefaultSubject,
    emailText: reminders.emailText || emailDefaultText,
});

export const normalizeClientsFollowUpSettings = (reminders: RemindersSettingsData) => ({
    enabled: reminders.clientsFollowUp?.enabled ?? false,
    daysAfterFirstAppointment: Math.max(1, reminders.clientsFollowUp?.daysAfterFirstAppointment ?? 1),
    communicationChannel: reminders.clientsFollowUp?.communicationChannel,
    communicationTemplate: reminders.clientsFollowUp?.communicationTemplate ?? '',
    communicationSubjectTemplate: reminders.clientsFollowUp?.communicationSubjectTemplate ?? '',
});
